import type { ValueOf } from 'type-fest'

import type { CurriculumApi } from '~/@entities/Curriculum/api'
import type { Entity as CurriculumEntity } from '~/@entities/Curriculum/dto'

import type { SchoolType } from '../SchoolSystem/schema'

export const SCHOOL_TYPE: ({
  label: '초' | '중' | '고'
  value: SchoolType
} & Pick<CurriculumEntity.CurriculumCoreItem, 'subscribeStatus'>)[] = [
  { label: '초', value: 'ELEMENTARY', subscribeStatus: 'SUBSCRIBED' },
  { label: '중', value: 'MIDDLE', subscribeStatus: 'SUBSCRIBED' },
  { label: '고', value: 'HIGH', subscribeStatus: 'SUBSCRIBED' },
] as const

export const SCHOOL_TYPE_EXPANDED: ({
  label: '초' | '중' | '고' | '전체'
  value: SchoolType | 'ALL'
} & Pick<CurriculumEntity.CurriculumCoreItem, 'subscribeStatus'>)[] = [
  { label: '전체', value: 'ALL', subscribeStatus: 'SUBSCRIBED' },
  ...SCHOOL_TYPE,
] as const

const ELEMENTARY_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_초등학교_1학년_1학기: '1.2.5.12.50',
  CURRICULUM_KEY_초등학교_1학년_2학기: '1.2.5.12.68',
  CURRICULUM_KEY_초등학교_2학년_1학기: '1.2.5.14.52',
  CURRICULUM_KEY_초등학교_2학년_2학기: '1.2.5.14.70',

  CURRICULUM_KEY_초등학교_1학년_1학기_22개정: '1.4.4145.4148.4157',
  CURRICULUM_KEY_초등학교_1학년_2학기_22개정: '1.4.4145.4148.4158',
  CURRICULUM_KEY_초등학교_2학년_1학기_22개정: '1.4.4145.4149.4159',
  CURRICULUM_KEY_초등학교_2학년_2학기_22개정: '1.4.4145.4149.4160',
  CURRICULUM_KEY_초등학교_5학년_2학기_22개정: '1.4.4145.4152.4166',
  CURRICULUM_KEY_초등학교_6학년_2학기_22개정: '1.4.4145.4153.4168',
} satisfies Record<string, CurriculumApi.TrieKey>

const MIDDLE_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_중학교_1학년: '1.2.9.27',
  CURRICULUM_KEY_중학교_2학년: '1.2.9.29',
  CURRICULUM_KEY_중학교_3학년: '1.2.9.31',
  CURRICULUM_KEY_중학교_1학년_22개정: '1.4.4146.4154',
  CURRICULUM_KEY_중학교_2학년_22개정: '1.4.4146.4155',
  CURRICULUM_KEY_중학교_1학년_1학기_22개정: '1.4.4146.4154.4169',
  CURRICULUM_KEY_중학교_1학년_2학기_22개정: '1.4.4146.4154.4170',

  CURRICULUM_KEY_중학교_1학년_2학기: '1.2.9.27.80',
  CURRICULUM_KEY_중학교_2학년_1학기_22개정: '1.4.4146.4155.4171',
  CURRICULUM_KEY_중학교_2학년_2학기_22개정: '1.4.4146.4155.4172',
  CURRICULUM_KEY_중학교_3학년_1학기_22개정: '1.4.4146.4156.4173',
  CURRICULUM_KEY_중학교_3학년_2학기_22개정: '1.4.4146.4156.4174',
} satisfies Record<string, CurriculumApi.TrieKey>

const HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_수학_상: '1.2.7.41',
  CURRICULUM_KEY_고등학교_수학_하: '1.2.7.42',
  CURRICULUM_KEY_고등학교_수학I: '1.2.7.43',
  CURRICULUM_KEY_고등학교_수학II: '1.2.7.44',
  CURRICULUM_KEY_고등학교_확률과통계: '1.2.7.45',
  CURRICULUM_KEY_고등학교_미적분: '1.2.7.46',
  CURRICULUM_KEY_고등학교_기하: '1.2.7.47',

  CURRICULUM_KEY_고등학교_공통수학1_22개정: '1.4.4147.4175',
  CURRICULUM_KEY_고등학교_공통수학2_22개정: '1.4.4147.4176',
  CURRICULUM_KEY_고등학교_대수_22개정: '1.4.4147.4177',
  CURRICULUM_KEY_고등학교_미적분1_22개정: '1.4.4147.4178',
  CURRICULUM_KEY_고등학교_확률과통계_22개정: '1.4.4147.4179',
  CURRICULUM_KEY_고등학교_미적분2_22개정: '1.4.4147.4180',
  CURRICULUM_KEY_고등학교_기하_22개정: '1.4.4147.4181',
} satisfies Record<string, CurriculumApi.TrieKey>

export const NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_교육과정_외: '1.2.7.48',
  CURRICULUM_KEY_고등학교_구_교육과정: '1.2.7.49',
  CURRICULUM_KEY_고등학교_구_교육과정_22개정: '1.4.4147.4182',
  CURRICULUM_KEY_고등학교_교육과정_외_22개정: '1.4.4147.4183',
} satisfies Record<string, CurriculumApi.TrieKey>

export const CURRICULUM_KEYS = {
  ...ELEMENTARY_SCHOOL_CURRICULUM_KEYS,

  ...MIDDLE_SCHOOL_CURRICULUM_KEYS,

  ...HIGH_SCHOOL_CURRICULUM_KEYS,
} satisfies Record<string, CurriculumApi.TrieKey>

// 1차 오픈 키(= 사전 오픈 = 풀오픈 이전)
export const PRE_OPEN_CURRICULUM_KEYS: readonly CurriculumApi.TrieKey[] = [
  MIDDLE_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_중학교_3학년_2학기_22개정,
]

export const DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY =
  HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상

export const SUBJECT_HIGH_KO = {
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상]: '고등수학(상)',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하]: '고등수학(하)',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학I]: '수학 I',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학II]: '수학 II',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계]: '확률과 통계',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분]: '미적분',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하]: '기하',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학1_22개정]: '공통수학1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학2_22개정]: '공통수학2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정]: '대수',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정]: '미적분1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정]: '확률과 통계',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분2_22개정]: '미적분2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정]: '기하',
} as const satisfies Record<
  ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>,
  Exclude<CurriculumApi.HighSchoolSubject, '(구) 교육과정' | '교육과정 외'>
>

export const NON_STANDARD_SUBJECT_HIGH_KO = {
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_교육과정_외]: '교육과정 외',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_구_교육과정]: '(구) 교육과정',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_구_교육과정_22개정]:
    '(구) 교육과정',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_교육과정_외_22개정]:
    '교육과정 외',
} as const satisfies Record<
  ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>,
  Extract<CurriculumApi.HighSchoolSubject, '(구) 교육과정' | '교육과정 외'>
>

export const ALL_SUBJECT_HIGH_KO_BASED_TRIE_KEY = {
  ...SUBJECT_HIGH_KO,
  ...NON_STANDARD_SUBJECT_HIGH_KO,
} as const satisfies Record<
  | ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>
  | ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>,
  CurriculumApi.HighSchoolSubject
>

export const SUBJECT_HIGH_SHORT = {
  ...Object.values(ALL_SUBJECT_HIGH_KO_BASED_TRIE_KEY).reduce(
    (acc, v) => {
      if (v === '고등수학(상)' || v === '고등수학(하)' || v === '확률과 통계') {
        const short = (() => {
          switch (v) {
            case '고등수학(상)':
              return '수학(상)'
            case '고등수학(하)':
              return '수학(하)'
            case '확률과 통계':
              return '확통'
            default:
              return v
          }
        })()
        return { ...acc, [v]: short }
      }
      return { ...acc, [v]: v }
    },
    {} as Record<
      CurriculumApi.HighSchoolSubject,
      | Exclude<CurriculumApi.HighSchoolSubject, '고등수학(상)' | '고등수학(하)' | '확률과 통계'>
      | '수학(상)'
      | '수학(하)'
      | '확통'
    >,
  ),
} as const

export const OUT_OF_CURRICULUM_TYPE = {
  교육과정내: 'CURRICULUM',
  교육과정외: 'EXTRA_CURRICULUM',
  이전교육과정: 'PREVIOUS_GRADE',
} as const

export const CURRICULUM_CONCEPT_TYPE = {
  소단원: 'LITTLE_CHAPTER',
  중단원: 'MIDDLE_CHAPTER',
} as const satisfies Record<
  '소단원' | '중단원',
  Extract<CurriculumApi.CurriculumType, 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'>
>

/**
 * 개정
 */
export const REVISION_KO = {
  CURRICULUM_15: '15개정',
  CURRICULUM_22: '22개정',
} as const satisfies Record<CurriculumApi.Revision, '15개정' | '22개정'>

export const REVISIONS = [
  {
    label: '15개정',
    value: 'CURRICULUM_15',
  },
  {
    label: '22개정',
    value: 'CURRICULUM_22',
  },
] as const satisfies readonly {
  readonly label: ValueOf<typeof REVISION_KO>
  readonly value: CurriculumApi.Revision
}[]

/**
 * 단원 트리 node의 깊이(depth) 값
 */
export const CURRICULUM_DEPTH = {
  학년학기: 0,
  대단원: 1,
  중단원: 2,
  소단원: 3,
  유형: 4,
  주제: 5,
  세부: 6,
} as const
